/**
 * PlaybookApplication - Manages playbook state
 * Simplified: Uses entity store directly, no Y.js (single-user editing)
 */

import { entityService } from '../../services/entityService';
import { PlaybookConfig, PlaybookEntity } from './types';

export class PlaybookApplication {
  private entityId?: string;
  private entity?: PlaybookEntity;
  private saveTimeout?: NodeJS.Timeout;
  private entityName: string = '';
  private configSubscribers: Set<(config: PlaybookConfig) => void> = new Set();
  
  constructor() {
    // No-argument constructor to match other applications
  }
  
  async initialize(entityId: string): Promise<void> {
    this.entityId = entityId;
    this.entity = await entityService.get(entityId) as PlaybookEntity;
    this.entityName = this.entity?.name || 'Untitled Playbook';
  }
  
  getEntityId(): string {
    return this.entityId || '';
  }
  
  /**
   * Get current prompt content
   */
  getPrompt(): string {
    return this.entity?.skeleton?.prompt || '';
  }
  
  /**
   * Update prompt content (debounced save)
   */
  updatePrompt(content: string): void {
    if (!this.entity) return;
    
    this.entity.skeleton.prompt = content;
    this.debouncedSave();
  }
  
  /**
   * Get current configuration
   */
  getPlaybookConfig(): PlaybookConfig {
    if (!this.entity) {
      return {
        prompt: '',
        enabled: false,
        triggerEntityTypes: ['File.PDF'],
        allowedTools: [],
        maxToolCalls: 100,
        maxDurationSeconds: 600
      };
    }
    
    return {
      prompt: this.entity.skeleton.prompt,
      enabled: this.entity.skeleton.enabled,
      triggerFolderId: this.entity.skeleton.triggerFolderId,
      triggerFolderPath: this.entity.skeleton.triggerFolderPath,
      triggerEntityTypes: this.entity.skeleton.triggerEntityTypes,
      allowedTools: this.entity.skeleton.allowedTools,
      maxToolCalls: this.entity.skeleton.maxToolCalls,
      maxDurationSeconds: this.entity.skeleton.maxDurationSeconds
    };
  }
  
  /**
   * Update configuration
   */
  updateConfig(updates: Partial<PlaybookConfig>): void {
    if (!this.entity) return;
    
    if (updates.enabled !== undefined) {
      this.entity.skeleton.enabled = updates.enabled;
    }
    if (updates.triggerFolderId !== undefined) {
      this.entity.skeleton.triggerFolderId = updates.triggerFolderId;
    }
    if (updates.triggerFolderPath !== undefined) {
      this.entity.skeleton.triggerFolderPath = updates.triggerFolderPath;
    }
    if (updates.triggerEntityTypes !== undefined) {
      this.entity.skeleton.triggerEntityTypes = updates.triggerEntityTypes;
    }
    if (updates.allowedTools !== undefined) {
      this.entity.skeleton.allowedTools = updates.allowedTools;
    }
    if (updates.maxToolCalls !== undefined) {
      this.entity.skeleton.maxToolCalls = updates.maxToolCalls;
    }
    if (updates.maxDurationSeconds !== undefined) {
      this.entity.skeleton.maxDurationSeconds = updates.maxDurationSeconds;
    }
    
    this.notifyConfigSubscribers();
    this.debouncedSave();
  }
  
  /**
   * Subscribe to configuration changes.
   * Returns an unsubscribe function.
   */
  onConfigChange(callback: (config: PlaybookConfig) => void): () => void {
    this.configSubscribers.add(callback);
    callback(this.getPlaybookConfig());
    return () => { this.configSubscribers.delete(callback); };
  }

  private notifyConfigSubscribers(): void {
    const config = this.getPlaybookConfig();
    this.configSubscribers.forEach(cb => cb(config));
  }
  
  /**
   * Save to backend (debounced)
   */
  private debouncedSave(): void {
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => {
      this.save();
    }, 1000);
  }
  
  /**
   * Save entity to backend
   */
  private async save(): Promise<void> {
    if (!this.entity || !this.entityId) return;
    
    try {
      await entityService.update(this.entityId, {
        skeleton: this.entity.skeleton
      });
      console.log('[PlaybookApp] ✅ Saved');
      this.notifyConfigSubscribers();
    } catch (error) {
      console.error('[PlaybookApp] Save failed:', error);
    }
  }
  
  /**
   * Handle entity_write operations for Playbook
   * Playbooks use structured content: { className: "playbook-prompt", attrs: { text: "..." } }
   */
  async handleEntityWrite(params: any): Promise<any> {
    if (!this.entity) {
      throw new Error('Entity not loaded');
    }
    
    const { operation, content } = params;
    const affectedNodes: string[] = [];
    
    // Extract text from structured content
    const extractText = (content: any): string => {
      if (typeof content === 'string') {
        // Legacy support for plain strings
        return content;
      }
      if (content?.attrs?.text) {
        return content.attrs.text;
      }
      throw new Error('Playbook content must be an object with className "playbook-prompt" and attrs.text');
    };
    
    switch (operation) {
      case 'replace':
        // Replace entire prompt content
        const replaceText = extractText(content);
        this.entity.skeleton.prompt = replaceText;
        affectedNodes.push('prompt');
        break;
        
      case 'insert':
        // For playbooks, insert appends to the end
        const insertText = extractText(content);
        this.entity.skeleton.prompt = (this.entity.skeleton.prompt || '') + '\n\n' + insertText;
        affectedNodes.push('prompt');
        break;
        
      case 'update':
        // Update is same as replace for playbooks (single text field)
        const updateText = extractText(content);
        this.entity.skeleton.prompt = updateText;
        affectedNodes.push('prompt');
        break;
        
      case 'delete':
        // Clear the prompt
        this.entity.skeleton.prompt = '';
        affectedNodes.push('prompt');
        break;
        
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
    
    // Save changes
    await this.save();
    
    return {
      affectedNodes,
      stats: {
        nodesModified: affectedNodes.length
      }
    };
  }
  
  /**
   * Get the current entity name (title)
   */
  getEntityName(): string {
    return this.entityName;
  }
  
  /**
   * Internal setter for entity name (doesn't call backend)
   * Used when external update already happened (e.g., sidebar rename)
   */
  _setEntityName(newName: string): void {
    this.entityName = newName;
    console.log('[PlaybookApplication] Entity name cache updated:', newName);
  }
  
  /**
   * Update the entity name (title)
   * Uses entityStore.actions to ensure events are emitted
   */
  async updateEntityName(newName: string): Promise<void> {
    const trimmedName = newName.trim();
    const finalName = trimmedName || 'Untitled Playbook';
    
    this.entityName = finalName;
    
    if (!this.entityId) {
      console.warn('[PlaybookApplication] Cannot update entity name - no entityId');
      return;
    }
    
    const { default: useEntityStore } = await import('../../stores/entityStore');
    await useEntityStore.getState().actions.updateEntity(this.entityId, { 
      name: finalName 
    });
    console.log('[PlaybookApplication] Entity name updated via entityStore.actions:', finalName);
  }
  
  /**
   * Cleanup — flush any pending debounced save and clear subscriber references.
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = undefined;
      // Fire save synchronously so the last edit is not lost.
      this.save().catch((e) => console.error('[PlaybookApp] Final save failed on destroy:', e));
    }
    this.configSubscribers.clear();
  }
}
